import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { DataService, DateService, HelperService, HTTPService, SocketService } from 'app/services';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components/confirm-dismiss-dialog/confirm-dismiss-dialog.component';
import { mileageUnits, vehicleTransmissions } from 'app/constants';
import { apptSocketEvents } from 'app/constants/socket.constants';

import { find, isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { contact, newSupplier, phoneTypes } from 'app/constants/supplier';

@Component({
  selector: 'app-supplier-sidebar',
  templateUrl: './supplier-sidebar.component.html',
  styleUrls: ['./supplier-sidebar.component.scss'],
  providers: [DialogService],
})
export class SupplierSidebarComponent implements OnInit, OnDestroy {
  supplier = this.helperService.deepCopy(newSupplier);
  @ViewChild('sidebarContainer', { static: true }) sidebarContainer: ElementRef;
  @Input() estimateId: string = '';
  supplierTypes: any[] = [
    {
      label: 'Internal',
      name: 'Internal',
      value: 'internal',
      placeholder: 'Internal',
    },
    {
      label: 'Online',
      name: 'Online',
      value: 'online',
      placeholder: 'Online',
    },
    {
      label: 'Archive',
      name: 'Archive',
      value: 'Archive',
      placeholder: 'Archive',
    },
  ];
  phoneTypes = phoneTypes;

  sideBarVisible: boolean = false;
  isLoading: boolean = false;
  isVehiclesLoading: boolean = false;
  isLoadingAppointment: boolean = true;
  customerAppts: any;
  index: number = 0;
  customer: any = {};
  vehicles: any[] = [];
  defaultCustomer: any = {};
  defaultVehicles: any[] = [];
  shopsMap: any = {};
  isAccountPayablePayment: boolean = false;
  tags: any[] = [];
  vehicleColors: any[] = [];
  driveTypes: any[] = [];
  vehicleTags: any[] = [];
  customerData: any;
  clickOutsideListener: any = null;
  isVehicleFetched: boolean = false;
  isCustomerFetched: boolean = false;
  isVehicleUpdated: boolean = false;
  isCustomerUpdated: boolean = false;
  highLightedVehicleId: string = '';
  defaultRegState: string = '';
  defaultRegStateCode: string = '';
  highLightedCustomerNotes: boolean = false;
  highLightedVIN: boolean = false;
  highLightedMiles: boolean = false;
  openTaxExemptDropdown: boolean = false;
  openConnectMotor: boolean = false;
  vehicleSearchPlaceholder: string = 'Search VIN or year, make, model';
  isCarfaxEnabled: boolean = false;
  isTireStorageEnabled: boolean = false;
  isDeferredServiceEnabled: boolean = false;
  isMarketingEnabled: boolean = false;
  isMultiLocationEnabled: boolean = false;
  vehicleOdmHistory: any = {};
  defaultMileageUnit: string = '';
  qboTaxExemptReasons: any[] = [];
  roOpenAction: string = '';
  isChatEnabled: boolean;
  fetchTags: boolean = true;
  openNotesTab: boolean = false;
  totalRecords: number = 0;
  skip: number = 0;
  customerId: string = '';
  acFocus: any = {
    title: false,
    notes: false,
    tech: false,
  };
  errors: any = {};
  notesRows: number;
  defaultNotes: string = '';
  notePanelData = {
    type: '',
    notes: '',
    title: '',
    sideModalStyleClass: '',
  };
  contectInfo: any = {};
  private unsubscribe: Subject<boolean> = new Subject<boolean>();

  constructor(
    private dataService: DataService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private dialogService: DialogService,
    private dateService: DateService,
    private socketService: SocketService
  ) {}

  ngOnInit() {
    this.onDataReceived();
    this.setClickOutsideListener();
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.unsubscribe();
  }

  /**
   * Listen to customer appt. created socket event
   */
  private listenCustomerApptCreatedEvent(shopId: string, customerId: string): void {
    this.socketService
      .socketOn(`${shopId}_${customerId}_${apptSocketEvents.CUSTOMER_APPT_CREATED}`)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ appointmentId }) => this.getCustomerAppointments({ limit: 20, offset: 0, customerId }));
  }

  /** Fetching customer data and vehicle data (if not fetched already) */
  getSupplier(supplierId: string) {
    this.isLoading = true;
    this.httpService
      .getSupplierById(supplierId)
      .then(
        (data: any) => {
          if (data) {
            // data && !data.type ? (data.type = 'internal') : null;
            this.supplier = data;
            // this.supplier.contectInfo = data.contactId;

            this.supplier.accPayableTerm = data.accPayableTerm || '';
            if ((this.supplier.type === 'internal' || this.supplier.type === 'QBO') && !this.supplier.archive) {
              this.supplier.tagColor = 'var(--tertiary-3-900)';
              this.supplier.type = this.supplierTypes[0];
            } else if (this.supplier.type === 'online' && !this.supplier.archive) {
              this.supplier.tagColor = 'var(--tertiary-2-800)';
              this.supplier.type = this.supplierTypes[1];
              this.supplier.qboResponse = data.quickbooksCompatibility && data.quickbooksCompatibility.qboResponse ? data.quickbooksCompatibility.qboResponse : '';
            } else if (this.supplier.type === 'sublet' && !this.supplier.archive) {
              this.supplier.tagColor = 'var(--tertiary-2-800)';
              this.supplier.type = {
                label: 'Sublet',
                name: 'Sublet',
                value: 'sublet',
                placeholder: 'Sublet',
              };
              this.supplier.qboResponse = data.quickbooksCompatibility && data.quickbooksCompatibility.qboResponse ? data.quickbooksCompatibility.qboResponse : '';
            } else if (this.supplier.archive) {
              this.supplier.tagColor = 'var(--secondary-500)';
              this.supplier.type = this.supplierTypes[2];
            }

            this.supplier.phones = [];
            this.supplier.emails = [];
            if (this.supplier.pos) {
              this.supplier.totalPayable = this.supplier.pos[0] && this.supplier.pos[0].totalPayable.length ? this.supplier.pos[0].totalPayable[0].amount : 0;
            }
            this.supplier.email = data.contactId.contactInfo.filter((email) => email.isPrimary && email.contactType === 'email');
            this.supplier.phone = data.contactId.contactInfo.filter((ph) => ph.isPrimary && ph.contactType === 'mobile');

            data.contactId.contactInfo.forEach((contactInfo: any) => {
              const contact = { ...contactInfo };

              if (contact.contactType === 'email') {
                contact.contactType = {
                  label: 'Email',
                  value: 'email',
                };
                if (contact.isPrimary) {
                  contact.value = contactInfo.value;
                }

                this.supplier.emails.push(contact);
              } else {
                contact.contactType = find(this.phoneTypes, {
                  value: contact.contactType,
                });

                if (contact.isPrimary) {
                  contact.value = this.helperService.removePhoneFormatting(contactInfo.value);
                }

                this.supplier['phones'].push(contact);
              }
            });
            if (data.contactId.contactInfo.length === 0) {
              (data.contactId.contactInfo.emails = [
                {
                  ...contact,
                  isPrimary: true,
                  contactType: { label: 'Email', value: 'email' },
                },
              ]),
                (data.contactId.contactInfo.phones = [
                  {
                    ...contact,
                    isPrimary: true,
                    contactType: { label: 'Mobile', value: 'mobile' },
                  },
                ]);
              (this.supplier['emails'] = [
                {
                  ...contact,
                  isPrimary: true,
                  contactType: { label: 'Email', value: 'email' },
                },
              ]),
                (this.supplier['phones'] = [
                  {
                    ...contact,
                    isPrimary: true,
                    contactType: { label: 'Mobile', value: 'mobile' },
                  },
                ]);
            }

            if (!data.billingAddress) {
              this.supplier.billingAddress = {
                addLine1: '',
                addLine2: '',
                city: '',
                state: '',
                zip: '',
                country: '',
                stateCode: '',
              };
            }

            if (!data.wareHouseAddress) {
              this.supplier.wareHouseAddress = {
                addLine1: '',
                addLine2: '',
                city: '',
                state: '',
                zip: '',
                country: '',
                stateCode: '',
              };
            }
          }
        },
        (error) => {
          this.helperService.showToaster('error', error);
        }
      )
      .finally(() => {
        this.isLoading = false;
      });
  }

  /** Getting customer vehicles */
  getVehicle() {
    if (this.isVehiclesLoading) return;

    this.isVehiclesLoading = true;

    const queryParams: any = {
      skip: this.skip,
      fetchTags: this.fetchTags,
    };

    if (!this.skip) {
      queryParams.vehicleId = this.highLightedVehicleId;
    }

    this.httpService
      .getCustomerVehicle(this.customerId, queryParams)
      .then(({ vehicles, totalRecords, tags, company }) => {
        if (this.fetchTags) {
          this.setVehicleTags(tags || []);
          this.setCompanySettings(company);
        }

        this.skip += 20;
        this.totalRecords = totalRecords;
        this.vehicles = [...this.vehicles, ...this.formatVehicles(vehicles)];
        this.defaultVehicles = this.helperService.deepCopy(this.vehicles);
      })
      .finally(() => {
        this.isVehiclesLoading = false;
      });
  }

  // getAllVehicles(customerId: string, fetchTags: boolean = false) {
  //   this.isVehiclesLoading = true;
  //   this.httpService.getVehiclesByCustomer(customerId, fetchTags).then(
  //     (data: any) => {
  //       this.isVehiclesLoading = false;

  //       if (!data) {
  //         return;
  //       }

  //       const { tags, company, vehicles } = data;
  //       const { appsettings, address } = company;

  //       if (tags && tags.length && fetchTags) {
  //         this.setVehicleTags(tags);
  //       }

  //       if (appsettings && appsettings.carfaxConnected) {
  //         this.isCarfaxEnabled = appsettings.carfaxConnected;
  //         this.vehicleSearchPlaceholder = 'Search license plate, VIN or YMM';
  //       }

  //       if (appsettings && appsettings.mileageMeasuringUnit) {
  //         this.defaultMileageUnit = appsettings.mileageMeasuringUnit;
  //       } else if (address && address.country === 'Canada') {
  //         this.defaultMileageUnit = 'km';
  //       } else if (address && address.country === 'US') {
  //         this.defaultMileageUnit = 'mi';
  //       }

  //       if (address && address.state) {
  //         this.defaultRegState = address.state;
  //         this.defaultRegStateCode = address.stateCode;
  //       }

  //       this.vehicles = this.formatVehicles(vehicles);
  //       this.defaultVehicles = this.helperService.deepCopy(this.vehicles);
  //     },
  //     (err) => {
  //       this.isVehiclesLoading = false;
  //       this.helperService.showToaster('error', err);
  //     }
  //   );
  // }

  formatVehicles(vehicles: any[]) {
    return vehicles.map((vehicle) => {
      const { ODM, VIN, year, color, vehicleName, driveType, transmission, connectedVia, isCarfaxConnected, name } = vehicle;

      vehicle.isCarfaxConnected = isCarfaxConnected;
      vehicle.connectedVia = connectedVia;
      vehicle.color = { name: color || '' };
      vehicle.driveType = { name: driveType || '' };
      vehicle.vin = VIN || '';
      vehicle.year = `${year || ''}`;
      vehicle.vehicleName = '';

      // if (this.customer.isFleetCustomer) {
      vehicle.vehicleName = vehicleName || name || '';
      // }

      vehicle.odmHistory = [];

      if (vehicle.licensePlateNo && !vehicle.registrationStateCode) {
        vehicle.registrationState = this.defaultRegState;
        vehicle.registrationStateCode = this.defaultRegStateCode;
        vehicle.state = vehicle.registrationState;
      }

      vehicle.transmission = find(vehicleTransmissions, { value: transmission }) || {};

      if (ODM && ODM.length) {
        this.vehicleOdmHistory[vehicle._id] = ODM.map((odm: any) => ({
          ...odm,
          readingDate: this.dateService.getCurrentDateTimeTz(new Date(odm.readingDate).getTime()),
        }));

        let { reading, mileageMeasuringUnit } = ODM.pop();

        vehicle.mileage = `${reading || ''}`;

        if (reading) {
          if (!mileageMeasuringUnit) {
            mileageMeasuringUnit = this.defaultMileageUnit;
          }

          vehicle.mileageMeasuringUnit = find(mileageUnits, {
            value: mileageMeasuringUnit,
          });
        }
      }

      return vehicle;
    });
  }

  setVehicleTags(tags: any[]) {
    this.vehicleColors = [];
    this.driveTypes = [];
    this.vehicleTags = [];

    tags.forEach((tag: any) => {
      if (tag.type === 'vehicle_color') {
        this.vehicleColors.push(tag);
      } else if (tag.type === 'drive_type') {
        this.driveTypes.push(tag);
      } else if (tag.type === 'vehicle') {
        this.vehicleTags.push(tag);
      }
    });

    this.vehicleTags = [...this.vehicleTags];
  }

  setCompanySettings(company: any) {
    if (company) {
      const { appsettings, address } = company;

      if (appsettings) {
        if (appsettings.carfaxConnected) {
          this.isCarfaxEnabled = appsettings.carfaxConnected;
          this.vehicleSearchPlaceholder = 'Search license plate, VIN or YMM';
        }

        if (appsettings.mileageMeasuringUnit) {
          this.defaultMileageUnit = appsettings.mileageMeasuringUnit;
        } else if (address) {
          this.defaultMileageUnit = address.country === 'Canada' ? 'km' : 'mi';
        }

        if (address && address.state) {
          this.defaultRegState = address.state;
          this.defaultRegStateCode = address.stateCode;
        }
      }
    }
  }

  updateCustomerTagList(tags: any[]) {
    this.tags = tags;
  }

  /** Getting customer appointments */
  getCustomerAppointments(customer: any) {
    this.isLoadingAppointment = true;
    this.httpService.getAppointmentsByCustomerId(customer).then(
      ({ data, current }) => {
        if (data && data.length) {
          this.customerAppts = {
            current,
            appointments: data,
          };
        }
        this.isLoadingAppointment = false;
      },
      (err) => {
        this.isLoadingAppointment = false;
        this.helperService.showToaster('error', err);
      }
    );
  }

  /** When user clicks outside of the sidebar */
  setClickOutsideListener() {
    this.clickOutsideListener = this.dataService.getClickEvent().subscribe((target) => {
      const sidebarShade = document.querySelector('.p-sidebar-mask');

      if (sidebarShade && sidebarShade.contains(target)) {
        this.onSidebarOutside();
      }
    });
  }

  /** Removing click outside listener */
  handleUnsubscribleClickoutside() {
    if (this.clickOutsideListener) {
      this.clickOutsideListener.unsubscribe();
    }

    this.clickOutsideListener = null;
  }

  /** Listening for data events from other action to fetch data or perform action accordingly */
  onDataReceived() {
    this.dataService
      .getData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ data }) => {
        if (!data) return;

        const { _id, fullName, key, tab, type } = data;

        if (data.key === 'company-appdata-updated') {
          this.isDeferredServiceEnabled = this.dataService.isDeferredServiceEnabled;
          this.isChatEnabled = this.dataService.isChatEnabled;
          this.isMarketingEnabled = this.dataService.isMarketingEnabled;
          this.isTireStorageEnabled = this.dataService.isTireStorageEnabled;
          this.isMultiLocationEnabled = this.dataService.isMultiLocationEnabled;
          this.shopsMap = this.dataService.shopsMap;
          this.isAccountPayablePayment = this.dataService.accountPayablePayment;
        }

        if (key !== 'sidebar-check-customer' && key !== 'sidebar-check-vehicle') {
          if (type === 'supplier-sidebar' && tab === 'supplier') {
            this.openNotesTab = false;
            const obj = data.data;
            this.index = 0;
            this.highLightedCustomerNotes = obj.action === 'scroll-to-notes';
            this.openTaxExemptDropdown = obj.action && obj.action.includes('tax-exempt-reason');
            this.roOpenAction = obj.action;
            this.customer._id = _id;
            this.supplier.fullName = fullName;
            this.getSupplier(_id);

            if (!this.clickOutsideListener) {
              this.setClickOutsideListener();
            }

            // listen to appointment created socket event
            this.listenCustomerApptCreatedEvent(this.helperService.getCompany().shopId, _id);
            this.fetchTags = true;
            this.skip = 0;
            this.totalRecords = 0;
            this.vehicles = [];
            this.defaultVehicles = [];
            this.sideBarVisible = true;
          } else if (type === 'supplier-sidebar' && tab === 'vehicle') {
            this.openNotesTab = false;
            if (data.data.type === 'vehicle-add') {
              this.isVehicleFetched = false;
            }
            const obj = data.data;
            this.index = 1;

            if (obj && obj.vehicle && obj.vehicle._id && obj.from === 'vehicle-side-list') {
              const [newVehicle] = this.formatVehicles([data.data.vehicle]);

              this.vehicles.splice(0, 0, this.helperService.deepCopy(newVehicle));
              this.defaultVehicles.splice(0, 0, this.helperService.deepCopy(newVehicle));
            } else {
              if (obj._id) {
                this.customer._id = obj._id;
                this.openConnectMotor = obj.openConnectMotor;
                this.highLightedVIN = obj.highLightedVIN;
                this.highLightedMiles = obj.highLightedMiles;
                // this.getCustomer(this.customer._id);
                this.highLightedVehicleId = obj.vehicleId;
              }

              this.fetchTags = true;
              this.skip = 0;
              this.totalRecords = 0;
              this.vehicles = [];
              this.defaultVehicles = [];
            }

            if (!this.clickOutsideListener) {
              this.setClickOutsideListener();
            }

            this.sideBarVisible = true;
          } else if (type === 'supplier-sidebar' && tab === 'close') {
            this.openNotesTab = false;
            const isVehicleUpdated = isEqual(this.vehicles, this.defaultVehicles);

            /** Unsubscribe listener on clickoutside */
            if (this.isCustomerUpdated) {
              if (data.data && data.data.type === 'supplier' && isVehicleUpdated) {
                this.sideBarVisible = false;
                this.openConnectMotor = false;
                this.highLightedVIN = false;
                this.highLightedMiles = false;
                this.highLightedCustomerNotes = false;
                this.openTaxExemptDropdown = false;
                this.handleResetForm();
                this.handleUnsubscribleClickoutside();
              } else {
                if (!data.data || (data.data && data.data.type !== 'supplier') || isVehicleUpdated) {
                  this.sideBarVisible = false;
                  this.openConnectMotor = false;
                  this.highLightedCustomerNotes = false;
                  this.openTaxExemptDropdown = false;
                  this.handleResetForm();
                  this.handleUnsubscribleClickoutside();
                }
              }
            } else {
              if (!data.data || (data.data && data.data.type !== 'customer') || isVehicleUpdated) {
                this.sideBarVisible = false;
                this.openConnectMotor = false;
                this.highLightedCustomerNotes = false;
                this.openTaxExemptDropdown = false;
                this.handleResetForm();
                this.handleUnsubscribleClickoutside();
              }
            }
          } else if (type === 'supplier-sidebar' && tab === 'sidebar-notes') {
            this.openNotesTab = true;
            this.sideBarVisible = true;
            this.defaultNotes = this.helperService.deepCopy(data.data.notes);
            this.notePanelData = this.helperService.deepCopy(data.data);
          } else if (
            (data.type === 'dialog' && (data.tab === 'estimate' || data.tab === 'supplier-sidebar') && data.from === 'appointment') ||
            (data.tab === 'calendar' && data.component === 'calendar-view' && data.data)
          ) {
            this.openNotesTab = false;
            if (this.customer._id && this.sideBarVisible) {
              const conf = {
                limit: 20,
                offset: 0,
                customerId: this.customer._id,
              };
              this.getCustomerAppointments(conf);
            }
          }
        }
      });
    this.isAccountPayablePayment = this.dataService.accountPayablePayment;
  }

  /** Reset data when sidebar is closed */
  handleHideSidebar() {
    this.handleResetForm();
    this.handleUnsubscribleClickoutside();
  }

  /** Setting active status of customer */
  handleSwitchChange(isActive: boolean) {
    this.supplier.isActive = isActive;
  }

  /** Handle tab change in sidebar */
  handleTabChange({ index }) {
    this.index = index;

    switch (index) {
      case 0:
        if (this.customer._id && !this.isCustomerFetched && !this.isLoading) {
          // this.getCustomer(this.customer._id);
        }
        return;

      // case 1:
      //   if (this.customer._id && !this.isVehicleFetched && !this.isVehiclesLoading) {
      //     this.customerId = this.customer._id;
      //     this.getVehicle();

      //   }
      //   return;

      // case 2:
      //   this.getAllVehicles(this.customerId, true);
      //   return;
    }
  }

  updateTags(data: any) {
    if (!data) return;

    if (data.type === 'vehicle_color') {
      for (let i = 0; i < this.vehicleColors.length; i++) {
        if (this.vehicleColors[i].type === data.type && data._id === this.vehicleColors[i]._id) {
          this.vehicleColors[i].name = data.name;
          this.vehicleColors = this.helperService.deepCopy(this.vehicleColors);
          break;
        }
      }
    } else if (data.type === 'vehicle') {
      for (let i = 0; i < this.vehicleTags.length; i++) {
        if (this.vehicleTags[i].type === data.type && data._id === this.vehicleTags[i]._id) {
          this.vehicleTags[i].name = data.name;
          this.vehicleTags = this.helperService.deepCopy(this.vehicleTags);

          this.vehicles.forEach((vehicle) => {
            if (vehicle.tags && vehicle.tags.length) {
              vehicle.tags.forEach((tag: any) => {
                if (tag.categoryId === data._id) {
                  tag.name = data.name;
                }
              });
            }
          });
          break;
        }
      }
    } else {
      for (let i = 0; i < this.driveTypes.length; i++) {
        if (this.driveTypes[i].type === data.type && data._id === this.driveTypes[i]._id) {
          this.driveTypes[i].name = data.name;
          this.driveTypes = this.helperService.deepCopy(this.driveTypes);
          break;
        }
      }
    }
  }

  updateTagList({ type, list }) {
    if (type === 'vehicle') {
      this.vehicleTags = [...list];
    }
  }

  handleResetForm() {
    this.index = 0;
    this.customer = {};
    this.vehicles = [];
    this.defaultVehicles = [];
    this.customerAppts = {
      appointments: [],
    };
    this.isVehicleUpdated = false;
    this.isCustomerUpdated = false;
    this.isCustomerFetched = false;
    this.isVehicleFetched = false;
    this.skip = 0;
    this.totalRecords = 0;
    this.fetchTags = true;
  }

  updateCustomerList(data: any = {}) {
    if (data && data.updateContactOnly) {
      this.customer.email = data.email;
      this.customer.phone = data.phone;
      this.customer.contactOnEmail = data.contactOnEmail;
      this.customer.contactOnPhone = data.contactOnPhone;
    }

    /** This data is subscribe in customer-list.component */
    this.defaultCustomer = this.helperService.deepCopy(this.customer);
    this.dataService.sendData(this.dataService.typeUpdate('customer-list', 'customer'));
  }

  supplierUpdate(data: any) {
    this.getSupplier(data?.contact?.contactEntityId);
  }

  getVehicleUpdate(vehicle: any) {
    if (this.customer._id) {
      for (let i = 0; i < this.vehicles.length; i++) {
        const { _id } = this.vehicles[i];

        if (_id === vehicle._id) {
          this.vehicles[i] = this.helperService.deepCopy(vehicle);
          this.defaultVehicles[i] = this.helperService.deepCopy(vehicle);
        }
      }
    }
  }

  handleCloseSidebar() {
    // this.onSidebarOutside();
    this.dataService.sendData(this.dataService.typeSupplierSideBar('close', null, null));
  }

  onSidebarOutside() {
    this.dataService.sendData(this.dataService.typeSupplierSideBar('close', null, null));
  }

  updateVehicleConnectedStatus() {
    this.openConnectMotor = false;
  }

  // navigateToCustomerCenter() {
    // this.helperService.openPageInNewExistingTab(`/customer/customer-center/${this.customer._id}`, this.customer._id);
  // }

  navigateToChat() {
    this.customerData;

    let isPrimaryMobile = false;
    let primaryContact = '';
    let contactNumber = '';

    this.customerData.contact.contactInfo.map((contact) => {
      if ((contact.contactType == 'mobile' || (contact.contactType && contact.contactType.value == 'mobile')) && contact.isPrimary == true && contact.value != '') {
        isPrimaryMobile = true;
        primaryContact = contact._id;
        contactNumber = contact.value;
      }
    });

    if (isPrimaryMobile) {
      this.dataService.customerData = {
        customerId: this.customerData.contact.contactEntityId,
        contactId: primaryContact,
        name: this.customer.fullName,
        contact: contactNumber,
      };
      setTimeout(() => {
        this.handleCloseSidebar();
      }, 0);
      /*
    NOTE: Here we are decoding the customerId because we want to open chat in new existing tab
    if customerId is opened in tab and if we hit chat button so it would not open in new tab thats why we use decoding
   */
      this.helperService.openPageInNewExistingTab(`chat?customerId=${btoa(this.customerData.contact.contactEntityId)}`, btoa(this.customerData.contact.contactEntityId));
    } else {
      this.helperService.showToaster('error', 'Customer does not have a mobile number');
    }
  }

  handleFocus(type: string) {
    this.acFocus[type] = true;
    this.errors[type] = '';
    if (type === 'notes') {
      this.notesRows = 3;
    }
  }

  handleChange(name: string, value: string) {
    this.notePanelData[name] = value;
  }

  saveNotes() {
    try {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          styleClassHeading: 'font-14',
          title: 'Save Changes',
          message: `The notes will be added to RO.`,
          button: {
            success: { title: 'Confirm' },
            cancel: { title: 'Cancel' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess }) => {
        if (isSuccess) {
          // this.dataService.sendData(this.dataService.typePassData('save-notes', { ...this.notePanelData, isSuccess }));

          this.onUpdate();
          this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
          // this.handleCloseSidebar();
        }
        onClose.unsubscribe();
      });
    } catch (error) {
      this.helperService.showToaster('error', error);
    }
  }

  mapSupplierContactTypes() {
    this.supplier.emails && this.supplier.emails.map((x) => (x.contactType = x.contactType.value ? x.contactType.value : x.contactType));
    this.supplier.phones && this.supplier.phones.map((x) => (x.contactType = x.contactType.value ? x.contactType.value : x.contactType));
  }

  onUpdate() {
    this.mapSupplierContactTypes();
    this.httpService.updateSupplier(this.formatSupplierData(this.supplier)).then(
      (supplier: any) => {
        if (supplier) {
          // this.isProcessing = false;
          this.dataService.sendData(this.dataService.typePassData('supplier-list', { data: this.supplier.name }));
          // this.dynamicDialogRef.close();
          this.helperService.showToaster('success', 'Supplier updated');
        }
      },
      (err) => {
        this.helperService.showToaster('error', err.messages);
      }
    );
  }
  formatSupplierData(data: any) {
    const supplier = { ...data };
    supplier.type = supplier.type.value;
    return supplier;
  }
}
